import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { providePrimeNG } from 'primeng/config';
import Material from '@primeuix/themes/material';
import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { definePreset } from '@primeuix/themes';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { DialogService } from 'primeng/dynamicdialog';
import {
  addContextInterceptor, Bootstrap, LayeredTranslateLoader, SYNEGEN_APPLICATION_CONFIG,
  SYNEGEN_TRANSLATION_SOURCES, TranslationOrder, Translations, TranslationSource
} from '@synegen/utility';
import { environment } from '../environments/environment';
import { authInterceptor } from '../ui-auth/auth.interceptor';
import { IMAGE_FALLBACK_CONFIG, provideFileConfig, provideFileTranslations } from '@synegen/file';
import { catchError, of } from 'rxjs';

Chart.register(ChartDataLabels);

const MyPreset = definePreset(Material, {
  components: {
    tabs: {
      tab: {
        activeColor: '#475DAA'
      },
      activeBar: {
        background: '#475DAA'
      }
    },
    carousel: {
      content: {
        gap: '0'
      }
    },
    textarea: {
      root: {
        borderRadius: '6px',
        background: 'white',
        placeholderColor: '#667085',
        borderColor: '#D0D5DD',
        focusRing: { color: 'rgba(51, 104, 198, 1)' }
      }
    },
    inputtext: {
      root: {
        borderRadius: '6px',
        background: 'white',
        placeholderColor: '#667085',
        borderColor: '#D0D5DD',
        focusRing: { color: 'rgba(51, 104, 198, 1)' }
      }
    },
    drawer: {
      content: {
        padding: '0'
      },
      header: {
        padding: '0'
      },
      footer: {
        padding: '0'
      }
    },
    avatar: {
      root: {
        background: 'linear-gradient(rgba(120, 159, 234, 1), rgba(71, 93, 170, 1))',
        color: 'white'
      }
    },
    progressbar: {
      root: {
        background: 'rgba(243, 244, 246, 1)',
        borderRadius: '30px'
      }
    },
    toolbar: {
      colorScheme: {
        light: {
          root: {
            background: 'white'
          }
        }
      }
    },
    chip: {
      root: {
        paddingX: '8px',
        paddingY: '2px'
      },
      colorScheme: {
        light: {
          root: {
            color: 'rgba(77, 89, 107, 1)',
            background: 'rgba(255, 255, 255, 1)'
          }
        }
      }
    },
    button: {
      colorScheme: {
        light: {
          outlined: {
            primary: {
              borderColor: 'rgba(208, 213, 221, 1)',
              color: 'rgba(74, 85, 101, 1)',

              activeBackground: 'rgba(239, 246, 255, 1)',

              hoverBackground: 'rgba(239, 246, 255, 1)'
            }
          },
          root: {
            primary: {
              background: 'transparent',
              borderColor: 'transparent',
              color: 'rgba(74, 85, 101, 1)',

              activeBackground: 'rgba(239, 246, 255, 1)',
              activeColor: 'rgba(52, 103, 197, 1)',
              activeBorderColor: 'rgba(239, 246, 255, 1)',

              hoverBackground: 'rgba(239, 246, 255, 1)',
              hoverBorderColor: 'rgba(239, 246, 255, 1)',
              hoverColor: 'rgba(52, 103, 197, 1)'
            },

            secondary: {
              background: '#3368C6',
              borderColor: '#3368C6',
              color: 'white',

              activeBackground: '#2759b0',
              activeColor: 'white',
              activeBorderColor: '#2759b0',

              hoverBackground: '#2759b0',
              hoverBorderColor: '#2759b0',
              hoverColor: 'white'
            },
            paddingX: '22px',
            paddingY: '12px',
            borderRadius: '8px'
          }
        },
        dark: {}
      }
    },
    card: {
      root: {
        borderRadius: '12px'
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: '{surface.700}'
          },
          subtitle: {
            color: '{surface.500}'
          }
        },
        dark: {
          root: {
            background: '{surface.900}',
            color: '{surface.0}'
          },
          subtitle: {
            color: '{surface.400}'
          }
        }
      }
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withFetch(), withInterceptors([addContextInterceptor, authInterceptor])),
    provideAppInitializer(() => {
      const bootstrap = inject(Bootstrap);
      return bootstrap.load('');
    }),
    {
      provide: SYNEGEN_APPLICATION_CONFIG,
      useValue: {
        apiContext: environment.apiContext,
        appContext: environment.appContext,
        devOptions: environment.devOptions,
        bootstrapUrl: environment.bootstrapUrl,
      },
    },
    {
      provide: IMAGE_FALLBACK_CONFIG,
      useValue: {
        profile: 'boy-blue.svg',
        featuredAnnouncement: 'thumbnail.jpg',
      },
    },
    provideFileConfig({ apiContext: environment.apiContext }),
    provideRouter(routes),
    provideTranslateService({
      fallbackLang: 'en',
      loader: { provide: TranslateLoader, useClass: LayeredTranslateLoader },
    }),

    // Library layers
    provideFileTranslations(),

    // App layer: public/i18n/en.json
    {
      provide: SYNEGEN_TRANSLATION_SOURCES,
      multi: true,
      useFactory: () => {
        const http = inject(HttpClient);
        return {
          order: TranslationOrder.APP,
          load: (lang: string) =>
            http.get<Translations>(`/i18n/${lang}.json`).pipe(catchError(() => of({}))),
        } satisfies TranslationSource;
      },
    },
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: 'this-is-a-bad-selector',
        },
      },
      ripple: true,
      inputVariant: 'outlined',
    }),
    MessageService,
    DialogService,
  ],
};
